import React, { useEffect, useState } from "react";
import {
  Github,
  Linkedin,
  Mail,
  Code,
  Database,
  Cloud,
  Terminal,
  Menu,
  X,
  Server,
  Layers,
  Zap,
  Package,
  GitBranch,
  Sun,
  Moon,
} from "lucide-react";

// ---------- Types ----------
type TechIcon = {
  icon: React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>;
  name: string;
  angle: number;
  color: string;
  url: string;
};

type Skill = {
  name: string;
  icon: TechIcon["icon"];
  level: number; // 0-100
  description: string;
  tag: string;
};

type Project = {
  title: string;
  description: string;
  tech: string[];
  highlights: string[];
  status: "Production" | "Development" | string;
  metrics: string;
};

type FormData = {
  name: string;
  email: string;
  purpose: string;
  message: string;
};

const Portfolio: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [activeSection, setActiveSection] = useState<string>("home");
  const [isDark, setIsDark] = useState<boolean>(true);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [nameColorIndex, setNameColorIndex] = useState<number>(0);
  const [showWelcomePopup, setShowWelcomePopup] = useState<boolean>(true);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    purpose: "",
    message: "",
  });
  const [formStatus, setFormStatus] = useState<string>("");

  // color cycle for name
  const nameColors = isDark
    ? ["#06b6d4", "#22d3ee", "#06b6d4", "#0891b2"]
    : ["#0284c7", "#0ea5e9", "#0284c7", "#0369a1"];

  useEffect(() => {
    setIsVisible(true);
    const colorInterval = setInterval(() => {
      setNameColorIndex((prev) => (prev + 1) % nameColors.length);
    }, 2000);
    return () => clearInterval(colorInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDark]); // depend on theme switch (length is constant per theme)

  // tech icon ring (same visuals, cleaned)
  const techIcons: TechIcon[] = [
    { icon: Code, name: "Python", angle: 0, color: "#3b82f6", url: "https://www.python.org/" },
    { icon: Server, name: "Django", angle: 45, color: "#10b981", url: "https://www.djangoproject.com/" },
    { icon: Layers, name: "DRF", angle: 90, color: "#8b5cf6", url: "https://www.django-rest-framework.org/" },
    { icon: Database, name: "PostgreSQL", angle: 135, color: "#06b6d4", url: "https://www.postgresql.org/" },
    { icon: Cloud, name: "AWS", angle: 180, color: "#f59e0b", url: "https://aws.amazon.com/" },
    { icon: Package, name: "Docker", angle: 225, color: "#ec4899", url: "https://www.docker.com/" },
    { icon: GitBranch, name: "CI/CD", angle: 270, color: "#14b8a6", url: "https://github.com/features/actions" },
    { icon: Zap, name: "Redis", angle: 315, color: "#f43f5e", url: "https://redis.io/" },
  ];

  const techLinks: Record<string, string> = {
    Python: "https://www.python.org/",
    Django: "https://www.djangoproject.com/",
    PostgreSQL: "https://www.postgresql.org/",
    AWS: "https://aws.amazon.com/",
    Docker: "https://www.docker.com/",
  };

  const coreStack: Skill[] = [
    { name: "Python", icon: Code, level: 95, description: "OOP, Design Patterns, Clean Code principles", tag: "Language" },
    { name: "Django", icon: Server, level: 90, description: "MVT, ORM, Middleware, Custom Commands", tag: "Framework" },
    { name: "DRF", icon: Layers, level: 90, description: "Serializers, ViewSets, Auth, Permissions", tag: "API" },
    { name: "PostgreSQL", icon: Database, level: 85, description: "Optimization, Indexing, JSONB operations", tag: "Database" },
    { name: "AWS", icon: Cloud, level: 80, description: "EC2, S3, RDS, Lambda, CloudWatch", tag: "Cloud" },
    { name: "Docker", icon: Package, level: 75, description: "Containerization, Multi-stage builds", tag: "DevOps" },
    { name: "CI/CD", icon: GitBranch, level: 75, description: "GitHub Actions, GitLab CI, Jenkins", tag: "DevOps" },
    { name: "Redis", icon: Zap, level: 70, description: "Caching, Sessions, Celery broker", tag: "Cache" },
  ];

  const projects: Project[] = [
    {
      title: "Tournament Platform",
      description:
        "Django backend with WebSocket for real-time updates. Custom ORM queries, Redis caching, S3 storage.",
      tech: ["Django", "DRF", "PostgreSQL", "Redis", "Celery", "AWS S3"],
      highlights: ["Custom management commands", "60% faster queries", "JWT authentication", "Async notifications"],
      status: "Production",
      metrics: "10k+ users",
    },
    {
      title: "Delivery System",
      description:
        "REST API for route optimization and GPS tracking. Custom middleware, PostgreSQL full-text search.",
      tech: ["Django", "DRF", "PostgreSQL", "Redis", "Docker", "Nginx"],
      highlights: ["Django signals", "PostGIS integration", "CI/CD pipeline", "Load balancing"],
      status: "Production",
      metrics: "5k+ API calls/day",
    },
    {
      title: "Analytics Dashboard",
      description: "High-performance backend with complex aggregations. Database indexing, query optimization.",
      tech: ["Django", "DRF", "PostgreSQL", "Celery", "Redis"],
      highlights: ["Materialized views", "Custom pagination", "Excel exports", "Data partitioning"],
      status: "Development",
      metrics: "1M+ records",
    },
  ];

  // ---------- Handlers ----------
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.purpose || !formData.message) {
      setFormStatus("Please fill in all fields");
      setTimeout(() => setFormStatus(""), 3000);
      return;
    }

    const subject = encodeURIComponent(`Portfolio Contact: ${formData.purpose}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\nPurpose: ${formData.purpose}\n\nMessage:\n${formData.message}`
    );

    const mailtoLink = `mailto:dhanendra@example.com?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;

    setFormStatus("Opening your email client...");
    setTimeout(() => {
      setFormData({ name: "", email: "", purpose: "", message: "" });
      setFormStatus("");
    }, 2000);
  };

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    setIsMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (!element) return;

    const offsetTop = element.offsetTop - 80;
    const startPosition = window.pageYOffset;
    const distance = offsetTop - startPosition;
    const duration = 1200;
    let start: number | null = null;

    const easeInOutQuart = (t: number) =>
      t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t;

    const animation = (currentTime: number) => {
      if (start === null) start = currentTime;
      const timeElapsed = currentTime - start;
      const progress = Math.min(timeElapsed / duration, 1);
      const ease = easeInOutQuart(progress);
      window.scrollTo(0, startPosition + distance * ease);
      if (progress < 1) requestAnimationFrame(animation);
    };

    requestAnimationFrame(animation);
  };

  // ---------- Theming ----------
  const bgClass = isDark
    ? "bg-gradient-to-br from-gray-900 via-black to-gray-900"
    : "bg-gradient-to-br from-gray-50 via-white to-gray-100";
  const textClass = isDark ? "text-gray-100" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-600";
  const borderClass = isDark ? "border-cyan-500/20" : "border-blue-300";
  const cardBg = isDark
    ? "bg-gradient-to-br from-gray-800/50 to-gray-900/50"
    : "bg-white/80 backdrop-blur-sm shadow-lg";

  return (
    <div className={`min-h-screen ${bgClass} ${textClass} transition-colors duration-500`} style={{ scrollBehavior: "smooth" }}>
      {/* Global styles for small entrance animations you already had */}
      <style>{`
        html { scroll-behavior: smooth; }
        body { overflow-x: hidden; }
        @media (prefers-reduced-motion: no-preference) {
          html { scroll-padding-top: 80px; }
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .animate-scaleIn { animation: scaleIn 0.3s ease-out; }
        @keyframes bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
      `}</style>

      {/* Welcome Popup */}
      {showWelcomePopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div
            className={`relative max-w-md w-full ${cardBg} border-2 ${
              isDark ? "border-cyan-500/50" : "border-blue-500/50"
            } rounded-3xl p-8 shadow-2xl animate-scaleIn`}
          >
            <button
              onClick={() => setShowWelcomePopup(false)}
              className={`absolute top-4 right-4 p-2 rounded-full ${
                isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-gray-200 hover:bg-gray-300"
              } transition-all`}
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <div className="flex justify-center mb-6">
              <div
                className={`p-4 rounded-full ${
                  isDark ? "bg-gradient-to-br from-cyan-500 to-blue-600" : "bg-gradient-to-br from-blue-500 to-indigo-600"
                } shadow-lg`}
              >
                <Terminal className="text-white" size={48} />
              </div>
            </div>

            <div className="text-center mb-6">
              <h3
                className={`text-3xl font-bold mb-3 bg-gradient-to-r ${
                  isDark ? "from-cyan-400 to-blue-500" : "from-blue-600 to-indigo-600"
                } bg-clip-text text-transparent`}
              >
                Welcome! 👋
              </h3>
              <p className={`${textSecondary} text-lg leading-relaxed`}>
                Thanks for visiting my portfolio! I'm{" "}
                <span className={`font-semibold ${isDark ? "text-cyan-400" : "text-blue-600"}`}>
                  Dhanendra Kumar Sonber
                </span>
                , a Backend Developer passionate about building scalable applications.
              </p>
            </div>

            <div className="flex justify-center gap-3 mb-6 flex-wrap">
              {["Python", "Django", "AWS"].map((tech) => (
                <span
                  key={tech}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    isDark
                      ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                      : "bg-blue-100 text-blue-600 border border-blue-300"
                  }`}
                >
                  {tech}
                </span>
              ))}
            </div>

            <button
              onClick={() => setShowWelcomePopup(false)}
              className={`w-full px-6 py-3 rounded-xl font-semibold transition-all hover:scale-105 ${
                isDark
                  ? "bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500"
                  : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500"
              } text-white shadow-lg`}
            >
              Explore Portfolio
            </button>
          </div>
        </div>
      )}

      {/* Nav */}
      <nav
        className={`fixed top-0 w-full ${isDark ? "bg-black/70" : "bg-white/70"} backdrop-blur-xl z-50 border-b ${borderClass}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isDark ? "bg-cyan-500/10" : "bg-blue-500/10"}`}>
                <Terminal className={`${isDark ? "text-cyan-400" : "text-blue-600"}`} size={28} />
              </div>
              <div>
                <div className={`text-lg font-bold ${textClass}`}>Dhanendra Kumar Sonber</div>
                <div className={`text-xs ${textSecondary} font-mono`}>Backend Developer</div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="hidden lg:flex items-center space-x-6">
                {["home", "stack", "projects", "experience", "contact"].map((section) => (
                  <button
                    key={section}
                    onClick={() => scrollToSection(section)}
                    className={`capitalize transition-all font-medium ${
                      activeSection === section
                        ? `${isDark ? "text-cyan-400" : "text-blue-600"} scale-110`
                        : `${textSecondary} hover:${isDark ? "text-cyan-400" : "text-blue-600"}`
                    }`}
                  >
                    {section}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-3 rounded-lg ${
                  isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-gray-200 hover:bg-gray-300"
                } transition-all`}
                aria-label="Toggle theme"
              >
                {isDark ? <Sun className="text-yellow-400" size={20} /> : <Moon className="text-blue-600" size={20} />}
              </button>

              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`lg:hidden p-3 rounded-lg ${isDark ? "bg-gray-800" : "bg-gray-200"}`}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {isMenuOpen && (
          <div className={`lg:hidden ${isDark ? "bg-black/95" : "bg-white/95"} backdrop-blur-xl border-t ${borderClass}`}>
            <div className="px-4 py-6 space-y-4">
              {["home", "stack", "projects", "experience", "contact"].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className={`block w-full text-left capitalize px-4 py-3 rounded-lg font-medium ${
                    activeSection === section
                      ? isDark
                        ? "bg-cyan-500/20 text-cyan-400"
                        : "bg-blue-500/20 text-blue-600"
                      : `${textSecondary} ${
                          isDark ? "hover:bg-cyan-500/10 hover:text-cyan-400" : "hover:bg-blue-500/10 hover:text-blue-600"
                        }`
                  }`}
                >
                  {section}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Home */}
      <section id="home" className="min-h-screen flex items-center justify-center pt-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-pink-600/20 blur-3xl" />
        {/* Decorative SVGs preserved */}
        <div className="absolute bottom-0 left-0 w-32 h-32 opacity-30">
          <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/200/svg">
            <path d="M100 200 Q80 150 100 100 Q120 150 100 200" fill="#10b981" opacity="0.6" />
            <path d="M100 200 Q60 160 80 120 Q100 160 100 200" fill="#059669" opacity="0.5" />
            <path d="M100 200 Q140 160 120 120 Q100 160 100 200" fill="#047857" opacity="0.5" />
            <ellipse cx="100" cy="195" rx="20" ry="8" fill="#065f46" />
          </svg>
        </div>

        <div className="max-w-5xl mx-auto w-full relative z-10">
          <div className="text-center space-y-8">
            <div className={`${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"} transition-all duration-1000`}>
              <h1
                className="text-6xl sm:text-7xl lg:text-8xl font-bold mb-6 transition-colors duration-500"
                style={{
                  color: nameColors[nameColorIndex],
                  textShadow: `0 0 80px ${nameColors[nameColorIndex]}40, 0 0 120px ${nameColors[nameColorIndex]}20`,
                }}
              >
                Dhanendra Kumar Sonber
              </h1>

              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="h-px w-16 bg-gradient-to-r from-transparent to-cyan-400" />
                <h2 className={`text-2xl sm:text-3xl font-light ${isDark ? "text-blue-300" : "text-blue-700"}`}>Backend Developer</h2>
                <div className="h-px w-16 bg-gradient-to-l from-transparent to-cyan-400" />
              </div>

              <p className={`${textSecondary} text-lg max-w-3xl mx-auto leading-relaxed mb-8`}>
                Crafting exceptional digital experiences with modern web technologies
              </p>

              <div className="flex flex-wrap justify-center gap-3 mb-10">
                {["Python", "Django", "PostgreSQL", "AWS", "Docker"].map((tech) => (
                  <a
                    key={tech}
                    href={techLinks[tech]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`px-5 py-2 rounded-full text-sm font-medium backdrop-blur-sm transition-all duration-300 cursor-pointer group relative overflow-hidden ${
                      isDark
                        ? "bg-gray-800/80 text-gray-300 border border-gray-700 hover:border-cyan-400"
                        : "bg-gray-100 text-gray-700 border border-gray-300 hover:border-blue-500"
                    }`}
                  >
                    <span
                      className={`relative z-10 transition-all duration-300 group-hover:font-bold ${
                        isDark ? "group-hover:text-cyan-400" : "group-hover:text-blue-600"
                      }`}
                    >
                      {tech}
                    </span>
                    <div
                      className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                        isDark ? "bg-gradient-to-r from-cyan-500/20 to-blue-500/20" : "bg-gradient-to-r from-blue-500/20 to-indigo-500/20"
                      }`}
                    />
                  </a>
                ))}
              </div>
            </div>

            <div
              className={`flex flex-wrap justify-center gap-6 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
              } transition-all duration-1000 delay-300`}
            >
              <button
                onClick={() => scrollToSection("projects")}
                className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full font-semibold overflow-hidden transition-all hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/50"
              >
                <span className="relative z-10 flex items-center gap-2">
                  View My Work
                  <svg className="w-5 h-5 group-hover:translate-y-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>

            {/* Icon strip */}
            <div className="flex justify-center items-center gap-6 mt-16 text-4xl flex-wrap">
              {techIcons.map((tech, idx) => {
                const delay = idx * 100;
                const bounceDelay = idx * 0.15;
                return (
                  <a
                    key={tech.name}
                    href={tech.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${
                      isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                    } transition-all duration-1000 hover:scale-125 cursor-pointer relative group`}
                    style={{
                      transitionDelay: `${delay + 600}ms`,
                      animation: `bounce 2s ease-in-out ${bounceDelay}s infinite`,
                    }}
                    title={tech.name}
                  >
                    <div className="relative">
                      <tech.icon style={{ color: tech.color, filter: `drop-shadow(0 0 8px ${tech.color}40)` }} size={40} />
                      <span
                        className={`absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap ${
                          isDark ? "text-cyan-400" : "text-blue-600"
                        }`}
                      >
                        {tech.name}
                      </span>
                    </div>
                  </a>
                );
              })}
            </div>

            <div className="text-center mt-20">
              <p className={`${isDark ? "text-gray-500" : "text-gray-400"} text-sm mb-3`}>Scroll to explore</p>
              <div className="flex justify-center">
                <div className="animate-bounce">
                  <svg className={`w-6 h-6 ${isDark ? "text-cyan-400" : "text-blue-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section id="stack" className={`py-24 px-4 ${isDark ? "bg-black/30" : "bg-gray-50/50"} relative overflow-hidden`}>
        {/* Decorative plant SVG retained */}
        <div className="absolute top-10 left-10 w-20 h-20 opacity-20">
          <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M100 180 Q88 135 100 90 Q112 135 100 180" fill="#10b981" opacity="0.7" />
            <path d="M100 180 Q70 145 88 100 Q100 140 100 180" fill="#059669" opacity="0.6" />
            <circle cx="100" cy="185" r="10" fill="#065f46" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2
              className={`text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r ${
                isDark ? "from-cyan-400 to-blue-500" : "from-blue-600 to-indigo-600"
              } bg-clip-text text-transparent`}
            >
              Tech Stack
            </h2>
            <p className={`${textSecondary} text-lg`}>Core technologies and expertise</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreStack.map((skill) => (
              <div
                key={skill.name}
                className={`${cardBg} border ${borderClass} rounded-2xl p-6 transition-all hover:shadow-xl hover:scale-105 ${
                  isDark ? "hover:border-cyan-500" : "hover:border-blue-500"
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <skill.icon
                    className={`${isDark ? "text-cyan-400" : "text-blue-600"} transition-transform group-hover:scale-110`}
                    size={36}
                  />
                  <span
                    className={`text-xs px-3 py-1 rounded-full font-medium ${
                      isDark ? "bg-cyan-500/20 text-cyan-400" : "bg-blue-100 text-blue-600"
                    }`}
                  >
                    {skill.tag}
                  </span>
                </div>
                <h3 className={`text-xl font-bold mb-2 ${textClass}`}>{skill.name}</h3>
                <p className={`${textSecondary} text-sm mb-4`}>{skill.description}</p>
                <div className={`w-full ${isDark ? "bg-gray-700" : "bg-gray-200"} rounded-full h-2 mb-2`}>
                  <div
                    className={`bg-gradient-to-r ${
                      isDark ? "from-cyan-400 to-blue-500" : "from-blue-500 to-indigo-600"
                    } h-2 rounded-full transition-all duration-1000`}
                    style={{ width: `${skill.level}%` }}
                  />
                </div>
                <p className={`text-sm ${isDark ? "text-cyan-400" : "text-blue-600"} font-semibold`}>{skill.level}%</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section id="projects" className="py-24 px-4 relative overflow-hidden">
        {/* Decorative plants preserved */}
        <div className="absolute bottom-10 left-5 w-24 h-24 opacity-15">
          <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M100 200 Q85 155 100 110 Q115 155 100 200" fill="#22c55e" opacity="0.6" />
            <path d="M100 200 Q65 165 85 120 Q100 160 100 200" fill="#16a34a" opacity="0.5" />
            <ellipse cx="100" cy="195" rx="18" ry="7" fill="#14532d" />
          </svg>
        </div>

        <div className="absolute top-20 right-5 w-20 h-20 opacity-20">
          <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M100 180 Q92 145 100 110 Q108 145 100 180" fill="#10b981" opacity="0.6" />
            <circle cx="100" cy="185" r="8" fill="#065f46" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2
              className={`text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r ${
                isDark ? "from-cyan-400 to-blue-500" : "from-blue-600 to-indigo-600"
              } bg-clip-text text-transparent`}
            >
              Production Projects
            </h2>
            <p className={`${textSecondary} text-lg`}>Real-world Django applications</p>
          </div>

          <div className="space-y-8">
            {projects.map((project) => (
              <div
                key={project.title}
                className={`${cardBg} border ${borderClass} rounded-2xl p-8 transition-all hover:shadow-xl ${
                  isDark ? "hover:border-cyan-500" : "hover:border-blue-500"
                }`}
              >
                <div className="flex flex-col sm:flex-row justify-between items-start mb-6 gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${isDark ? "bg-cyan-500/10" : "bg-blue-500/10"}`}>
                      <Code className={`${isDark ? "text-cyan-400" : "text-blue-600"}`} size={32} />
                    </div>
                    <div>
                      <h3 className={`text-2xl font-bold ${textClass} mb-2`}>{project.title}</h3>
                      <p className={`${isDark ? "text-cyan-400" : "text-blue-600"} text-sm font-semibold`}>{project.metrics}</p>
                    </div>
                  </div>
                  <span
                    className={`text-xs px-4 py-2 rounded-full font-semibold ${
                      project.status === "Production"
                        ? isDark
                          ? "bg-green-500/20 text-green-400 border border-green-500/30"
                          : "bg-green-100 text-green-600 border border-green-300"
                        : isDark
                        ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                        : "bg-yellow-100 text-yellow-600 border border-yellow-300"
                    }`}
                  >
                    {project.status}
                  </span>
                </div>

                <p className={`${textSecondary} mb-6 text-lg`}>{project.description}</p>

                <div className="mb-6">
                  <h4 className={`${isDark ? "text-cyan-400" : "text-blue-600"} font-semibold mb-3`}>Highlights:</h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {project.highlights.map((h, i) => (
                      <p key={i} className={`flex items-start gap-2 ${textSecondary}`}>
                        <span className={`${isDark ? "text-cyan-400" : "text-blue-600"} mt-1`}>→</span>
                        <span>{h}</span>
                      </p>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {project.tech.map((tech) => (
                    <span
                      key={tech}
                      className={`text-xs px-3 py-1 rounded-lg font-medium border ${
                        isDark ? "bg-cyan-500/10 text-cyan-400 border-cyan-500/20" : "bg-blue-50 text-blue-600 border-blue-200"
                      }`}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience */}
      <section id="experience" className={`py-24 px-4 ${isDark ? "bg-black/30" : "bg-gray-50/50"}`}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className={`text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r ${
                isDark ? "from-cyan-400 to-blue-500" : "from-blue-600 to-indigo-600"
              } bg-clip-text text-transparent`}
            >
              Experience
            </h2>
          </div>

          <div className="relative">
            <div
              className={`absolute left-8 top-0 bottom-0 w-0.5 ${
                isDark ? "bg-gradient-to-b from-cyan-500 via-blue-500 to-purple-500" : "bg-gradient-to-b from-blue-500 via-indigo-500 to-purple-500"
              }`}
            />
            <div className="space-y-12">
              {/* Exp 1 */}
              <div className="relative pl-20">
                <div
                  className={`absolute left-0 top-0 w-16 h-16 rounded-full ${
                    isDark ? "bg-gradient-to-br from-cyan-500 to-blue-600" : "bg-gradient-to-br from-blue-500 to-indigo-600"
                  } flex items-center justify-center border-4 ${isDark ? "border-gray-900" : "border-gray-50"} shadow-lg`}
                >
                  <Terminal className="text-white" size={24} />
                </div>

                <div className={`${cardBg} border ${borderClass} rounded-2xl p-6 md:p-8 transition-all hover:shadow-xl hover:scale-105`}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                    <span
                      className={`${
                        isDark ? "text-cyan-400 bg-cyan-500/20" : "text-blue-600 bg-blue-100"
                      } text-sm font-bold mb-2 sm:mb-0 inline-block px-4 py-1 rounded-full`}
                    >
                      2023 - Present
                    </span>
                    <span
                      className={`text-xs px-3 py-1 rounded-full font-semibold ${
                        isDark ? "bg-green-500/20 text-green-400 border border-green-500/30" : "bg-green-100 text-green-600 border border-green-300"
                      }`}
                    >
                      Current
                    </span>
                  </div>
                  <h3
                    className={`text-2xl font-bold ${textClass} transition-colors ${
                      isDark ? "group-hover:text-cyan-400" : "group-hover:text-blue-600"
                    } mb-2`}
                  >
                    Backend Developer (Freelance)
                  </h3>
                  <p className={`${textSecondary} mb-6 text-lg`}>
                    Building production Django apps with clean architecture and DevOps practices.
                  </p>
                  <div className="space-y-3">
                    {[
                      "RESTful APIs serving 50k+ daily requests",
                      "CI/CD pipelines reducing deployment time by 70%",
                      "Database optimization achieving 3x performance",
                    ].map((item) => (
                      <p key={item} className={`flex gap-3 ${textSecondary}`}>
                        <span className={`${isDark ? "text-cyan-400" : "text-blue-600"} text-xl flex-shrink-0`}>✓</span>
                        <span>{item}</span>
                      </p>
                    ))}
                  </div>
                </div>
              </div>

              {/* Exp 2 */}
              <div className="relative pl-20">
                <div
                  className={`absolute left-0 top-0 w-16 h-16 rounded-full ${
                    isDark ? "bg-gradient-to-br from-blue-500 to-purple-600" : "bg-gradient-to-br from-indigo-500 to-purple-600"
                  } flex items-center justify-center border-4 ${isDark ? "border-gray-900" : "border-gray-50"} shadow-lg`}
                >
                  <Code className="text-white" size={24} />
                </div>

                <div className={`${cardBg} border ${borderClass} rounded-2xl p-6 md:p-8 transition-all hover:shadow-xl hover:scale-105`}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                    <span
                      className={`${
                        isDark ? "text-cyan-400 bg-cyan-500/20" : "text-blue-600 bg-blue-100"
                      } text-sm font-bold mb-2 sm:mb-0 inline-block px-4 py-1 rounded-full`}
                    >
                      2022 - 2023
                    </span>
                    <span
                      className={`text-xs px-3 py-1 rounded-full font-semibold ${
                        isDark ? "bg-gray-500/20 text-gray-400 border border-gray-500/30" : "bg-gray-100 text-gray-600 border border-gray-300"
                      }`}
                    >
                      1 Year
                    </span>
                  </div>
                  <h3
                    className={`text-2xl font-bold ${textClass} transition-colors ${
                      isDark ? "group-hover:text-cyan-400" : "group-hover:text-blue-600"
                    } mb-2`}
                  >
                    Backend Developer
                  </h3>
                  <p className={`${textSecondary} mb-6 text-lg`}>
                    API design, database architecture, and cloud infrastructure management.
                  </p>
                  <div className="space-y-3">
                    {[
                      "Built microservices with Django and FastAPI",
                      "OAuth2 authentication and RBAC",
                      "CloudWatch monitoring and alerting",
                    ].map((item) => (
                      <p key={item} className={`flex gap-3 ${textSecondary}`}>
                        <span className={`${isDark ? "text-cyan-400" : "text-blue-600"} text-xl flex-shrink-0`}>✓</span>
                        <span>{item}</span>
                      </p>
                    ))}
                  </div>
                </div>
              </div>
              {/* Add more experience blocks here if needed */}
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 px-4 relative overflow-hidden">
        <div className="absolute bottom-5 right-10 w-28 h-28 opacity-20">
          <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M100 200 Q88 160 100 120 Q112 160 100 200" fill="#10b981" opacity="0.6" />
            <path d="M100 200 Q72 170 88 130 Q100 165 100 200" fill="#059669" opacity="0.5" />
            <path d="M100 200 Q128 170 112 130 Q100 165 100 200" fill="#047857" opacity="0.5" />
            <ellipse cx="100" cy="195" rx="22" ry="8" fill="#065f46" />
          </svg>
        </div>

        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2
              className={`text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r ${
                isDark ? "from-cyan-400 to-blue-500" : "from-blue-600 to-indigo-600"
              } bg-clip-text text-transparent`}
            >
              Let's Connect
            </h2>
            <p className={`${textSecondary} text-xl`}>Open to freelance projects and collaboration opportunities</p>
          </div>

          <div className={`${cardBg} border ${borderClass} rounded-2xl p-8 md:p-12`}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className={`block text-sm font-semibold mb-2 ${textClass}`}>
                  Your Name <span className={`${isDark ? "text-cyan-400" : "text-blue-600"}`}>*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                  required
                  className={`w-full px-4 py-3 rounded-xl border transition-all ${
                    isDark
                      ? "bg-gray-800/50 border-gray-700 text-gray-100 placeholder-gray-500 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  } outline-none`}
                />
              </div>

              <div>
                <label className={`block text-sm font-semibold mb-2 ${textClass}`}>
                  Email Address <span className={`${isDark ? "text-cyan-400" : "text-blue-600"}`}>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                  required
                  className={`w-full px-4 py-3 rounded-xl border transition-all ${
                    isDark
                      ? "bg-gray-800/50 border-gray-700 text-gray-100 placeholder-gray-500 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  } outline-none`}
                />
              </div>

              <div>
                <label className={`block text-sm font-semibold mb-2 ${textClass}`}>
                  Purpose <span className={`${isDark ? "text-cyan-400" : "text-blue-600"}`}>*</span>
                </label>
                <select
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-4 py-3 rounded-xl border transition-all ${
                    isDark
                      ? "bg-gray-800/50 border-gray-700 text-gray-100 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                      : "bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  } outline-none cursor-pointer`}
                >
                  <option value="">Select a purpose</option>
                  <option value="Freelance Project">Freelance Project</option>
                  <option value="Collaboration">Collaboration</option>
                  <option value="Job Opportunity">Job Opportunity</option>
                  <option value="Consultation">Consultation</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-semibold mb-2 ${textClass}`}>
                  Message <span className={`${isDark ? "text-cyan-400" : "text-blue-600"}`}>*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={5}
                  placeholder="Tell me about your project or inquiry..."
                  required
                  className={`w-full px-4 py-3 rounded-xl border transition-all resize-none ${
                    isDark
                      ? "bg-gray-800/50 border-gray-700 text-gray-100 placeholder-gray-500 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  } outline-none`}
                ></textarea>
              </div>

              {formStatus && (
                <div
                  className={`text-center py-2 px-4 rounded-xl ${
                    formStatus.includes("fill")
                      ? isDark
                        ? "bg-red-500/20 text-red-400"
                        : "bg-red-100 text-red-600"
                      : isDark
                      ? "bg-green-500/20 text-green-400"
                      : "bg-green-100 text-green-600"
                  }`}
                >
                  {formStatus}
                </div>
              )}

              <button
                type="submit"
                className={`w-full px-8 py-4 rounded-xl font-semibold transition-all hover:scale-105 shadow-lg ${
                  isDark
                    ? "bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white"
                    : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white"
                }`}
              >
                Send Message
              </button>
            </form>
          </div>

          <div className="mt-12 text-center">
            <p className={`${textSecondary} mb-6`}>Or reach out directly via</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="mailto:dhanendra@example.com"
                className={`flex items-center gap-2 px-6 py-3 ${
                  isDark ? "bg-gray-800 hover:bg-gray-700 border-gray-700" : "bg-white hover:bg-gray-50 border-gray-300"
                } border rounded-xl font-medium hover:scale-105 transition-all`}
              >
                <Mail size={20} />
                <span>Email</span>
              </a>
              <a
                href="https://linkedin.com/in/dhanendra"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 px-6 py-3 ${
                  isDark ? "bg-gray-800 hover:bg-gray-700 border-gray-700" : "bg-white hover:bg-gray-50 border-gray-300"
                } border rounded-xl font-medium hover:scale-105 transition-all`}
              >
                <Linkedin size={20} />
                <span>LinkedIn</span>
              </a>
              <a
                href="https://github.com/dhanendra"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 px-6 py-3 ${
                  isDark ? "bg-gray-800 hover:bg-gray-700 border-gray-700" : "bg-white hover:bg-gray-50 border-gray-300"
                } border rounded-xl font-medium hover:scale-105 transition-all`}
              >
                <Github size={20} />
                <span>GitHub</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-8 px-4 border-t ${borderClass} ${isDark ? "bg-black/50" : "bg-white/50"}`}>
        <div className={`max-w-6xl mx-auto text-center ${textSecondary}`}>
          <p>© 2025 Dhanendra Kumar Sonber · Built with React & Tailwind CSS</p>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;
